import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  approveGuardianLink,
  cancelGuardianInvite,
  fetchStudentGuardians,
  inviteGuardian,
  rejectGuardianLink,
  revokeGuardianLink,
  resendGuardianInvite,
} from '../../api/guardian';
import Button from '../common/Button';
import { ListLoadingSpinner } from '../layout/ListPageLayout';
import { formatDateTime } from '../../utils/date';
import { GUARDIAN_INVITE } from '../../config/accountAccessCopy';
import { apiErrorMessage } from '../../utils/apiError';

const STATUS_CLASSES = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  REVOKED: 'bg-gray-100 text-gray-700',
};


const alertApiError = (err, fallback) => {
  alert(apiErrorMessage(err, fallback));
};

const formatRelationship = (value, t) => {
  if (!value) return '—';
  const key = String(value).toUpperCase();
  return t(`guardianPublic.relationships.${key}`, {
    defaultValue: String(value)
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  });
};

const GuardiansSection = ({ studentId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchStudentGuardians(studentId);
      setData(res);
    } catch (err) {
      setError(apiErrorMessage(err, t('guardiansSection.loadFailed')));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const onInvite = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      alert(t('guardiansSection.enterEmail'));
      return;
    }
    setInviting(true);
    try {
      await inviteGuardian(studentId, trimmed);
      setEmail('');
      await load();
    } catch (err) {
      alertApiError(err, GUARDIAN_INVITE.inviteFailure);
    } finally {
      setInviting(false);
    }
  };

  const onResend = async (inviteId) => {
    setActionId(`resend-${inviteId}`);
    try {
      await resendGuardianInvite(studentId, inviteId);
      await load();
    } catch (err) {
      alertApiError(err, GUARDIAN_INVITE.resendFailure);
    } finally {
      setActionId(null);
    }
  };

  const onCancel = async (inviteId) => {
    if (!window.confirm(t('guardiansSection.cancelInviteConfirm'))) return;
    setActionId(`cancel-${inviteId}`);
    try {
      await cancelGuardianInvite(studentId, inviteId);
      await load();
    } catch (err) {
      alertApiError(err, t('guardiansSection.cancelInviteFailed'));
    } finally {
      setActionId(null);
    }
  };

  const onApprove = async (linkId) => {
    setActionId(`approve-${linkId}`);
    try {
      await approveGuardianLink(linkId);
      await load();
    } catch (err) {
      alertApiError(err, t('guardiansSection.approveFailed'));
    } finally {
      setActionId(null);
    }
  };

  const onRevoke = async (linkId) => {
    if (!window.confirm(t('guardiansSection.revokeConfirm'))) return;
    setActionId(`revoke-${linkId}`);
    try {
      await revokeGuardianLink(linkId);
      await load();
    } catch (err) {
      alertApiError(err, t('guardiansSection.revokeFailed'));
    } finally {
      setActionId(null);
    }
  };

  const onReject = async (linkId) => {
    if (!window.confirm(t('guardiansSection.rejectConfirm'))) return;
    setActionId(`reject-${linkId}`);
    try {
      await rejectGuardianLink(linkId);
      await load();
    } catch (err) {
      alertApiError(err, t('guardiansSection.rejectFailed'));
    } finally {
      setActionId(null);
    }
  };

  const guardians = data?.guardians || [];
  const invites = data?.pendingInvites || [];
  const hasAny = guardians.length > 0 || invites.length > 0;
  const slotsUsed = data?.slotsUsed ?? 0;
  const maxGuardians = data?.maxGuardians ?? 2;

  return (
    <div className="section-card max-w-4xl mt-6">
      <h2 className="form-section-title mb-1">{t('guardiansSection.title')}</h2>
      <p className="text-sm text-gray-600 mb-4">
        {t('guardiansSection.intro', { max: maxGuardians })}
        {data?.canApprove ? t('guardiansSection.canApprove') : ''}
        {!data?.canRevoke && !data?.canApprove && hasAny
          ? t('guardiansSection.contactAcademy')
          : ''}
      </p>

      {loading && <ListLoadingSpinner label={t('guardiansSection.loading')} />}
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {!loading && !error && (
        <>
          {!hasAny && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center mb-4">
              <p className="text-gray-700 font-medium">{t('guardiansSection.emptyTitle')}</p>
              <p className="text-sm text-gray-500 mt-1">
                {t('guardiansSection.emptyBody')}
              </p>
            </div>
          )}

          {guardians.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('guardiansSection.linkedTitle')}</h3>
              <ul className="space-y-3">
                {guardians.map((g) => (
                  <li
                    key={g.linkId}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-gray-200 bg-white p-4"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{g.guardianName || g.guardianEmail}</div>
                      <div className="text-sm text-gray-500">{g.guardianEmail}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {formatRelationship(g.relationship, t)}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CLASSES[g.status] || STATUS_CLASSES.REVOKED}`}>
                        {t(`guardiansSection.status.${g.status}`, { defaultValue: g.status })}
                      </span>
                      {data?.canApprove && g.status === 'PENDING_APPROVAL' && (
                        <>
                          <Button
                            type="button"
                            variant="primary"
                            className="!py-1.5 !px-3 text-sm"
                            disabled={!!actionId}
                            onClick={() => onApprove(g.linkId)}
                          >
                            {actionId === `approve-${g.linkId}` ? '…' : t('guardiansSection.approve')}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            className="!py-1.5 !px-3 text-sm"
                            disabled={!!actionId}
                            onClick={() => onReject(g.linkId)}
                          >
                            {actionId === `reject-${g.linkId}` ? '…' : t('guardiansSection.reject')}
                          </Button>
                        </>
                      )}
                      {data?.canRevoke && (g.status === 'APPROVED' || g.status === 'PENDING_APPROVAL') && (
                        <Button
                          type="button"
                          variant="secondary"
                          className="!py-1.5 !px-3 text-sm"
                          disabled={!!actionId}
                          onClick={() => onRevoke(g.linkId)}
                        >
                          {actionId === `revoke-${g.linkId}` ? '…' : t('guardiansSection.remove')}
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {invites.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('guardiansSection.pendingTitle')}</h3>
              <ul className="space-y-3">
                {invites.map((inv) => (
                  <li
                    key={inv.inviteId}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{inv.invitedEmail}</div>
                      <div className="text-sm text-gray-500">
                        {t(`guardiansSection.inviteType.${inv.inviteType}`, { defaultValue: inv.inviteType })}
                        {inv.expiresAt && !inv.expired && (
                          <>{t('guardiansSection.expires', { date: formatDateTime(inv.expiresAt) })}</>
                        )}
                        {inv.expired && <span className="text-amber-700">{t('guardiansSection.expired')}</span>}
                      </div>
                    </div>
                    {data?.canInvite && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          className="!py-1.5 !px-3 text-sm"
                          disabled={!!actionId}
                          onClick={() => onResend(inv.inviteId)}
                        >
                          {actionId === `resend-${inv.inviteId}` ? '…' : GUARDIAN_INVITE.resendButton}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="!py-1.5 !px-3 text-sm"
                          disabled={!!actionId}
                          onClick={() => onCancel(inv.inviteId)}
                        >
                          {actionId === `cancel-${inv.inviteId}` ? '…' : t('common.cancel')}
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data?.canInvite && slotsUsed < maxGuardians && (
            <form onSubmit={onInvite} className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
              <input
                type="email"
                className="input flex-1"
                placeholder={t('guardiansSection.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Button type="submit" variant="primary" disabled={inviting}>
                {inviting ? GUARDIAN_INVITE.sending : GUARDIAN_INVITE.inviteButton}
              </Button>
            </form>
          )}

          {data?.canInvite && slotsUsed >= maxGuardians && (
            <p className="text-sm text-amber-700 mt-2">
              {t('guardiansSection.limitReached', { used: slotsUsed, max: maxGuardians })}
              {data?.canRevoke
                ? t('guardiansSection.limitRevokeHint')
                : t('guardiansSection.limitCancelHint')}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default GuardiansSection;
